import { useState, useEffect } from 'react'
import { Image as ImageIcon, Trash2, Plus, Edit2 } from 'lucide-react'
import { adminApi } from '../../lib/api'
import PostFormModal from '../PostFormModal'
import type { Post } from '../../types'
import { useToast } from '../ui/ToastProvider'

interface PostsTabProps {
  tripId: string
}

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    if (!Number.isNaN(parsed)) return parsed
    const dateParsed = Date.parse(value)
    if (!Number.isNaN(dateParsed)) return dateParsed
  }
  return null
}

const parseLocation = (raw: unknown) => {
  if (!raw) return undefined
  let location: any = raw
  if (typeof raw === 'string') {
    try {
      location = JSON.parse(raw)
    } catch {
      return {
        id: undefined,
        name: raw,
        latitude: null,
        longitude: null,
      }
    }
  }
  if (typeof location !== 'object' || location === null) return undefined
  const latitude = parseNumber(location.latitude ?? location.lat)
  const longitude = parseNumber(location.longitude ?? location.lng)
  return {
    id: location.id ?? location.location_id ?? null,
    name: location.name ?? location.address ?? 'Location',
    latitude,
    longitude,
  }
}

const parseMedia = (media: unknown): Post['media'] => {
  if (!Array.isArray(media)) return []
  return media.map((item: any) => ({
    id: item.id ?? item.media_id ?? '',
    postId: item.post_id ?? item.postId ?? '',
    fileId: item.file_id ?? item.fileId ?? '',
    mediaType: (item.media_type ?? item.mediaType ?? 'photo') as Post['media'][number]['mediaType'],
    orderIndex: item.order_index ?? item.orderIndex ?? 0,
    caption: item.caption ?? null,
    width: item.width ?? null,
    height: item.height ?? null,
    url: item.url ?? item.media_url ?? item.file_url ?? undefined,
    thumbnailUrl: item.thumbnail_url ?? item.preview_url ?? undefined,
    createdAt: parseNumber(item.created_at) ?? Date.now(),
  }))
}

const normalizePost = (raw: any): Post => {
  const media = parseMedia(raw.media)
  const createdAt = parseNumber(raw.created_at) ?? Date.now()
  const updatedAt = parseNumber(raw.updated_at) ?? createdAt

  const userId = raw.user_id ?? raw.userId ?? raw.user?.id ?? ''
  const displayName =
    raw.user?.displayName ??
    raw.user?.display_name ??
    raw.author_name ??
    raw.user_name ??
    'Unknown user'
  const avatarUrl =
    raw.user?.avatarUrl ??
    raw.user?.avatar_url ??
    raw.author_avatar ??
    raw.user_avatar ??
    null

  return {
    id: raw.id,
    userId,
    contentType: (raw.content_type ?? raw.contentType ?? 'text') as Post['contentType'],
    description: raw.description ?? raw.content ?? null,
    privacy: (raw.privacy ?? raw.visibility ?? 'public') as Post['privacy'],
    tripId: raw.trip_id ?? raw.tripId ?? null,
    eventId: raw.event_id ?? raw.eventId ?? null,
    locationId: raw.location_id ?? raw.locationId ?? null,
    mediaCount: raw.mediaCount ?? raw.media_count ?? media.length,
    createdAt,
    updatedAt,
    isBlocked: raw.is_blocked ?? raw.isBlocked ?? false,
    media,
    user: {
      id: userId,
      displayName,
      avatarUrl,
    },
    trip: raw.trip,
    event: raw.event,
    location: parseLocation(raw.location || raw.location_json || raw.locationJson),
    stats: {
      likes: raw.stats?.likes ?? raw.likes ?? 0,
      comments: raw.stats?.comments ?? raw.comments ?? 0,
    },
    isLikedByViewer: raw.isLikedByViewer ?? raw.viewer_has_liked ?? false,
  }
}

export default function PostsTab({ tripId }: PostsTabProps) {
  const { showToast } = useToast()
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingPost, setEditingPost] = useState<Post | null>(null)

  const handlePostSuccess = () => {
    showToast('Post created successfully!', { type: 'success' })
    loadPosts()
  }

  const handlePostUpdated = () => {
    showToast('Post updated successfully!', { type: 'success' })
    setIsEditModalOpen(false)
    setEditingPost(null)
    loadPosts()
  }

  useEffect(() => {
    loadPosts()
  }, [tripId])

  const loadPosts = async () => {
    setLoading(true)
    try {
      const response = await adminApi.getPosts({ trip_id: tripId, limit: 50 })
      const payload = response.data?.data ?? response.data ?? []
      const rawPosts = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.posts)
          ? payload.posts
          : []
      const normalizedPosts = rawPosts.map(normalizePost)
      setPosts(normalizedPosts)
    } catch (error) {
      console.error('Failed to load posts:', error)
      setPosts([])
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (postId: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return

    setPosts(prev => prev.filter(post => post.id !== postId))
    try {
      await adminApi.deletePost(postId)
      loadPosts()
    } catch (error) {
      console.error('Failed to delete post:', error)
      // Reload to stay in sync if deletion failed
      loadPosts()
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <>
        <div className="text-center py-8 text-gray-500">Loading posts...</div>
        <PostFormModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          tripId={tripId}
          onSuccess={loadPosts}
        />
      </>
    )
  }

  if (posts.length === 0) {
    return (
      <>
        <div className="text-center py-12">
          <div className="text-gray-400 mb-2">
            <ImageIcon className="h-12 w-12 mx-auto" />
          </div>
          <p className="text-gray-500 mb-4">No posts yet for this trip</p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn btn-primary flex items-center gap-2 mx-auto"
          >
            <Plus className="h-4 w-4" />
            Create First Post
          </button>
        </div>
        <PostFormModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          tripId={tripId}
          onSuccess={loadPosts}
        />
      </>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          Trip Posts ({posts.length})
        </h3>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="btn btn-primary btn-sm flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Post
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {posts.map((post) => (
          <div
            key={post.id}
            className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
          >
            <div className="flex gap-4">
              {/* Media Preview */}
              {post.media.length > 0 && (
                <div className="flex-shrink-0">
                  <div className="relative w-24 h-24 bg-gray-100 rounded-lg overflow-hidden">
                    {(() => {
                      const preview = post.media[0].url || post.media[0].thumbnailUrl
                      if (!preview) {
                        return (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <ImageIcon className="h-8 w-8" />
                          </div>
                        )
                      }
                      return (
                        <img
                          src={preview}
                          alt="Post media"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      )
                    })()}
                    {post.mediaCount > 1 && (
                      <div className="absolute top-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
                        <ImageIcon className="h-3 w-3" />
                        {post.mediaCount}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    {/* User & Date */}
                    <div className="flex items-center gap-2 mb-2">
                      {post.user.avatarUrl && (
                        <img
                          src={post.user.avatarUrl}
                          alt={post.user.displayName}
                          className="w-5 h-5 rounded-full"
                        />
                      )}
                      <span className="text-sm font-medium text-gray-900">
                        {post.user.displayName}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-xs text-gray-500">
                        {formatDate(post.createdAt)}
                      </span>
                      <span className={`badge badge-sm ${
                        post.contentType === 'gallery' ? 'badge-success' :
                        post.contentType === 'photo' ? 'badge-info' :
                        'badge-secondary'
                      }`}>
                        {post.contentType}
                      </span>
                    </div>

                    {/* Description */}
                    {post.description && (
                      <p className="text-sm text-gray-700 line-clamp-2 mb-2">
                        {post.description}
                      </p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>❤️ {post.stats.likes}</span>
                      <span>💬 {post.stats.comments}</span>
                      {post.location && (
                        <span>📍 {post.location.name}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setEditingPost(post)
                        setIsEditModalOpen(true)
                      }}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Edit post"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete post"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create Post Modal */}
      <PostFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        tripId={tripId}
        onSuccess={handlePostSuccess}
      />

      <PostFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          setEditingPost(null)
        }}
        tripId={tripId}
        onSuccess={handlePostUpdated}
        mode="edit"
        post={editingPost}
      />
    </div>
  )
}
